import {assert} from 'Asserts';
import {abbreviateList} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {molUnitTypePredicate} from 'inventory/InventoryUnitUtil';
import {DEFAULT_DEFAULT_VALUE} from 'notebook/data-tables/DataTableConstants';
import {
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  ChemistryReactionRole,
  StoichiometryCalculationMode,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {getCasRegistryNumberColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/CasRegistryNumberCustomEval';
import {getIupacNameColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/IupacNameCustomEval';
import limitingReactantCustomEval from 'notebook/integrations/chemistry/custom-evals/LimitingReactantCustomEval';
import {getMolecularFormulaColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/MolecularFormulaCustomEval';
import {getMolecularWeightColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/MolecularWeightCustomEval';
import {getMoleculePurityColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/MoleculePurityCustomEval';
import makeStoichiometryCalculationCustomEval, {
  StoichiometryColumnGroupCellDependencies,
  StoichiometryColumnGroupLimitingReactantCellDependencies,
  StoichiometryTableCircularColumnGroup,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/makeStoichiometryCalculationCustomEval';
import {
  DEFAULT_MASS_UNIT_SYMBOL,
  DEFAULT_MOL_UNIT_SYMBOL,
  stoichiometryAmountUnitTypePredicate,
  stoichiometryConcentrationUnitTypePredicate,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryUnits';
import {getDefaultUnitCustomEval} from 'notebook/integrations/chemistry/custom-evals/StoichiometryUnitDefaultsCustomEval';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  UnitReferenceColumnDisplayMode,
  CreateStructuredTableProps,
  CustomEvalTableView,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableRow,
  GetCustomEvalArgsOptions,
  CustomStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {toSpreadsheetBooleanValue} from 'notebook/tables/excess/Util';
import {
  RowMetadata,
  TABLE_MINI_COLUMN_WIDTH,
  TABLE_SMALL_COLUMN_WIDTH,
  CellIndex,
  CustomEvalFnAndType,
  CustomEvalType,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {BooleanCellDisplayMode} from 'schemas/tags/BooleanValueCellEditor';
import {hasKey} from 'util/Types';

import molesActualCustomEval from './custom-evals/MolesActualCustomEval';
import stoichiometryConcentrationCustomEval from './custom-evals/StoichiometryConcentrationCustomEval';
import yieldCustomEval from './custom-evals/YieldCustomEval';

export interface StoichiometryStructuredTableRowMetadata extends RowMetadata {}

export interface SyncedReactionComponents {
  [ChemistryReactionRole.REACTANT]: Array<string>;
  [ChemistryReactionRole.PRODUCT]: Array<string>;
}

export interface StoichiometryCustomEvalOptions {
  tableMetadata: StoichiometryStructuredTableMetadata;
  rowMetadata: StoichiometryStructuredTableRowMetadata;
}
export interface StoichiometryStructuredTableMetadata {
  syncedComponents?: SyncedReactionComponents;
  /** How computed amount / moles / concentration values are rounded. */
  calculationMode?: StoichiometryCalculationMode;
}

export type StoichiometryStructuredTable = StructuredTable<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
>;

export type StoichiometryTableRow = StructuredTableRow<StoichiometryStructuredTableRowMetadata>;

type ColumnGroupToIndexMap = Partial<Record<StructuredTableColumnGroups, number>>;

const LIMITING_REACTANT_ROW_INDEX = 0;

const REACTION_ROLE_OPTIONS = Object.values(ChemistryReactionRole).map(
  (role) => CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]
);

/** There should only be 1 of each of these columns in a StoichiometryStructuredTable */
const STOICHIOMETRY_TABLE_COLUMN_GROUPS = new Set([
  StructuredTableColumnGroups.CHEMISTRY_MOLECULE,
  StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE,
  StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
  StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT,
  StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY,
  StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN,
  StructuredTableColumnGroups.CHEMISTRY_MOLES_ACTUAL,
  StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_ACTUAL,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA,
  StructuredTableColumnGroups.CHEMISTRY_IUPAC_NAME,
  StructuredTableColumnGroups.CHEMISTRY_CAS_REGISTRY_NUMBER,
  StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION,
  StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_YIELD,
]);

const COLUMN_GROUP_TO_NAME_MAP = {
  [StructuredTableColumnGroups.CHEMISTRY_MOLECULE]: 'Compound',
  [StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE]: 'Role',
  [StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT]: 'LR',
  [StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE]: 'Equiv',
  [StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT]: 'MW',
  [StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY]: 'Purity (%)',
  [StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN]: 'Moles (plan)',
  [StructuredTableColumnGroups.CHEMISTRY_MOLES_ACTUAL]: 'Moles (actual)',
  [StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT]: 'Moles Unit',
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN]: 'Amt (plan)',
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_ACTUAL]: 'Amt (act)',
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT]: 'Amt Unit',
  [StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA]: 'Formula',
  [StructuredTableColumnGroups.CHEMISTRY_IUPAC_NAME]: 'IUPAC Name',
  [StructuredTableColumnGroups.CHEMISTRY_CAS_REGISTRY_NUMBER]: 'CAS Number',
  [StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION]: 'Concentration',
  [StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT]: 'Conc. Unit',
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_YIELD]: 'Yield (%)',
} satisfies Partial<Record<StructuredTableColumnGroups, string>>;

const DEFAULT_PURITY_VALUE = {text: '100'};
const DEFAULT_MW_PRECISION = 3;
const DEFAULT_EQUIV_PRECISION = 2;
const DEFAULT_QUANTITY_PRECISION = 4;

export default class StoichiometryStructuredTableAdapter extends GenericStructuredTableAdapter<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
> {
  override createTable(
    createProps: CreateStructuredTableProps<StoichiometryStructuredTableMetadata>
  ): Omit<StoichiometryStructuredTable, 'cid'> {
    // Initialize syncedComponents to prevent immediate sync on table creation
    const metadata: StoichiometryStructuredTableMetadata = {
      syncedComponents: createProps.metadata.syncedComponents ?? {
        [ChemistryReactionRole.REACTANT]: [],
        [ChemistryReactionRole.PRODUCT]: [],
      },
      calculationMode: StoichiometryCalculationMode.SIGFIGS,
      ...createProps.metadata,
    };
    return super.createTable({...createProps, metadata});
  }

  getBlankRowMetadata(
    tableMetadata: StoichiometryStructuredTableMetadata
  ): StoichiometryStructuredTableRowMetadata {
    return {};
  }

  override getDisplayName(structuredTable: StoichiometryStructuredTable): string {
    return 'Reaction';
  }

  override allowSingleInputs(_: StoichiometryStructuredTable): boolean {
    return false;
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: StoichiometryStructuredTableMetadata;
  }): StructuredTableColumn {
    switch (column.columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE: {
        return {
          ...column,
          frontEndMetadata: {
            validSchemas: 'ALL',
          },
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE: {
        assert(
          column.columnType === TableColumnType.OPTION_SELECTOR,
          'Reaction Role column must be an OPTION_SELECTOR'
        );
        return {
          ...column,
          options: REACTION_ROLE_OPTIONS,
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT: {
        return {
          ...column,
          frontEndMetadata: {
            displayMode: UnitReferenceColumnDisplayMode.SYMBOL,
            unitTypesFilterPredicate: molUnitTypePredicate,
          },
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT: {
        return {
          ...column,
          frontEndMetadata: {
            displayMode: UnitReferenceColumnDisplayMode.SYMBOL,
            unitTypesFilterPredicate: stoichiometryAmountUnitTypePredicate,
          },
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT: {
        return {
          ...column,
          frontEndMetadata: {
            displayMode: UnitReferenceColumnDisplayMode.SYMBOL,
            unitTypesFilterPredicate: stoichiometryConcentrationUnitTypePredicate,
          },
        };
      }
      default:
        return column;
    }
  }

  private makeStoichiometryStructuredTableColumn(
    columnOptions: Omit<Parameters<typeof makeStructuredTableColumn>[0], 'name'> & {name?: string}
  ): StructuredTableColumn {
    const {columnGroup} = columnOptions;
    return makeStructuredTableColumn({
      ...columnOptions,
      name:
        columnOptions.name ??
        ((columnGroup &&
          hasKey(COLUMN_GROUP_TO_NAME_MAP, columnGroup) &&
          COLUMN_GROUP_TO_NAME_MAP[columnGroup]) ||
          ''),
    });
  }

  /**
   * ⚠️ CHEMISTRY_TABLE: There is a need to create more general column types that can be used for purely
   * typed input. This may be Custom Columns, but it may be something else (schema-less schema fields)
   *
   * Helper to make a decimal column
   */
  private makeDecimalStoichiometryStructuredTableColumn(
    overrides: Partial<CustomStructuredTableColumn> = {}
  ): CustomStructuredTableColumn {
    const {columnGroup} = overrides;
    return {
      ...this.makeStoichiometryStructuredTableColumn({
        columnGroup,
        columnType: TableColumnType.CUSTOM,
      }),
      columnType: TableColumnType.CUSTOM,
      customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
      ...overrides,
    };
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: StoichiometryStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return [
      {
        ...this.makeStoichiometryStructuredTableColumn({
          columnType: TableColumnType.EXISTING_BIOENTITY,
          columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLECULE,
        }),
        columnType: TableColumnType.EXISTING_BIOENTITY,
        bioentityTypeFilters: [ModelTypeConstants.MOLECULE],
      },
      {
        ...this.makeStoichiometryStructuredTableColumn({
          columnType: TableColumnType.OPTION_SELECTOR,
          columnGroup: StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE,
        }),
        columnType: TableColumnType.OPTION_SELECTOR,
        options: REACTION_ROLE_OPTIONS,
      },
      {
        ...this.makeStoichiometryStructuredTableColumn({
          columnType: TableColumnType.CUSTOM,
          columnGroup: StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT,
          width: TABLE_MINI_COLUMN_WIDTH,
          isReadOnly: true,
        }),
        columnType: TableColumnType.CUSTOM,
        customColumnConfig: {
          dataType: CustomColumnDataType.BOOLEAN,
          displayMode: BooleanCellDisplayMode.CHECKBOX,
        },
      },
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
        width: TABLE_SMALL_COLUMN_WIDTH,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_EQUIV_PRECISION},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_MW_PRECISION},
        isReadOnly: true,
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY,
        defaultValue: {...DEFAULT_PURITY_VALUE},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLES_ACTUAL,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
        isReadOnly: true,
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_ACTUAL,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.STRING,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA,
        isReadOnly: true,
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.STRING,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_IUPAC_NAME,
        isReadOnly: true,
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.STRING,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_CAS_REGISTRY_NUMBER,
        isReadOnly: true,
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT,
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_YIELD,
        isReadOnly: true,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_EQUIV_PRECISION},
      }),
    ];
  }

  private getStoichiometryTableColumnGroupToIndexMap(
    columns: ReadonlyArray<StructuredTableColumn>
  ): ColumnGroupToIndexMap {
    return columns.reduce<ColumnGroupToIndexMap>((acc, column, index) => {
      const {columnGroup} = column;
      if (columnGroup && STOICHIOMETRY_TABLE_COLUMN_GROUPS.has(columnGroup as StructuredTableColumnGroups)) {
        const structuredTableColumnGroup = columnGroup as StructuredTableColumnGroups;
        assert(
          acc[structuredTableColumnGroup] == null,
          `There should only be at most 1 column for each Stoichiometry Table Column Group.`,
          {structuredTableColumnGroup}
        );
        acc[structuredTableColumnGroup] = index;
      }
      return acc;
    }, {});
  }

  /** Maps a list of column groups to cell dependencies for a given row. This is used for simple cases where
   * the dependencies are straightforward and not circular.
   *
   * @param columnGroupToIndexMap - A map of column groups to their indices in the structured table.
   * @param rowIndex - The index of the row to get cell dependencies for.
   * @param columnGroups - The list of column groups to get cell dependencies for.
   * @returns A list of cell dependencies for the given row and column groups.
   */
  private getColumnGroupCellDependencies(
    columnGroupToIndexMap: ColumnGroupToIndexMap,
    rowIndex: number,
    columnGroups: Array<StructuredTableColumnGroups>
  ): Array<CellIndex | string> {
    return columnGroups.map((columnGroup) => {
      const columnIndex = columnGroupToIndexMap[columnGroup];
      return columnIndex != null ? {row: rowIndex, column: columnIndex} : '';
    });
  }

  private getCustomEvalDependencies(
    tableView: CustomEvalTableView<
      StoichiometryStructuredTableMetadata,
      StoichiometryStructuredTableRowMetadata
    >,
    currentCellIndex: CellIndex
  ): Array<CellIndex | string> {
    const {column: columnIndex, row: rowIndex} = currentCellIndex;
    const column = tableView.columns[columnIndex];
    const {columnGroup} = column;
    if (!columnGroup) {
      return [];
    }
    const columnGroupToIndexMap = this.getStoichiometryTableColumnGroupToIndexMap(tableView.columns);
    switch (columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA:
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT:
      case StructuredTableColumnGroups.CHEMISTRY_IUPAC_NAME:
      case StructuredTableColumnGroups.CHEMISTRY_CAS_REGISTRY_NUMBER: {
        return this.getColumnGroupCellDependencies(columnGroupToIndexMap, rowIndex, [
          StructuredTableColumnGroups.CHEMISTRY_MOLECULE,
        ]);
      }
      case StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT: {
        const reactionRoleColumnIndex =
          columnGroupToIndexMap[StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE];
        return [
          toSpreadsheetBooleanValue(rowIndex === LIMITING_REACTANT_ROW_INDEX),
          reactionRoleColumnIndex ? {row: rowIndex, column: reactionRoleColumnIndex} : '',
        ];
      }
      case StructuredTableColumnGroups.CHEMISTRY_MOLES_ACTUAL: {
        return this.getColumnGroupCellDependencies(columnGroupToIndexMap, rowIndex, [
          StructuredTableColumnGroups.CHEMISTRY_AMOUNT_ACTUAL,
          StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
          StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT,
          StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
          StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY,
          StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION,
          StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT,
        ]);
      }
      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION:
      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT: {
        return this.getColumnGroupCellDependencies(columnGroupToIndexMap, rowIndex, [
          StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
        ]);
      }
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_YIELD: {
        return this.getColumnGroupCellDependencies(columnGroupToIndexMap, rowIndex, [
          StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE,
          StructuredTableColumnGroups.CHEMISTRY_MOLES_ACTUAL,
          StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN,
        ]);
      }
      // These 3 columns can all solve for each other, but we cannot make circular dependencies for the
      // cell references. To combat this, we check to see if any of the variables are manually filled by the
      // user (either directly or via a formula). If so, that variable becomes the "anchor" variable and
      // calculations will be based on that variable (and other columns will become locked).
      case StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE:
      case StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN:
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN: {
        return this.getStoichiometryCalculationCustomEvalDependencies(
          tableView,
          currentCellIndex,
          columnGroup,
          columnGroupToIndexMap
        );
      }
      default:
        return [];
    }
  }

  private getStoichiometryCalculationCustomEvalDependencies(
    tableView: CustomEvalTableView<
      StoichiometryStructuredTableMetadata,
      StoichiometryStructuredTableRowMetadata
    >,
    currentCellIndex: CellIndex,
    columnGroup: StoichiometryTableCircularColumnGroup,
    columnGroupToIndexMap: ColumnGroupToIndexMap
  ): Array<CellIndex | string> {
    const {row: rowIndex} = currentCellIndex;
    const limitingReactantRowIndex = LIMITING_REACTANT_ROW_INDEX;
    const isLimitingReactant = limitingReactantRowIndex === rowIndex;
    // The limiting reactant equivalence will always be 1, so no cell dependencies are needed.
    if (isLimitingReactant && columnGroup === StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE) {
      return ['', toSpreadsheetBooleanValue(true)];
    }

    const circularColumns: Set<StoichiometryTableCircularColumnGroup> = new Set([
      StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
      StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN,
      StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN,
    ]);
    // Don't consider the limiting reactant equivalence as a circular variable since it is always 1.
    if (isLimitingReactant) {
      circularColumns.delete(StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE);
    }
    // Check which of the circularly-dependent columns has been manually specified by the user.
    const filledCircularColumns = [...circularColumns].filter((colGroup) => {
      const columnIndex = columnGroupToIndexMap[colGroup];
      return Boolean(columnIndex && !TableUtil.isCellEmpty(tableView.rows[rowIndex].cells[columnIndex]));
    });
    // If exactly 1 of the circularly-dependent variables has been filled, we can solve for the result.
    if (filledCircularColumns.length === 1) {
      const [filledColumnGroup] = filledCircularColumns;
      // If the variable we are solving for is the one that's filled, no dependencies are needed.
      if (filledColumnGroup === columnGroup) {
        return [];
      }
      // Otherwise, fill the dependencies with each column group (minus the other circularly-dependent columns)
      const limitingReactantDependencies = StoichiometryColumnGroupLimitingReactantCellDependencies.map(
        (columnGroup) => {
          // Limiting Reactants shouldn't have any dependencies to themselves.
          if (isLimitingReactant) {
            return '';
          }
          const columnIndex = columnGroupToIndexMap[columnGroup];
          return columnIndex != null ? {row: limitingReactantRowIndex, column: columnIndex} : '';
        }
      );
      const componentDependencies = StoichiometryColumnGroupCellDependencies.map((columnGroup) => {
        if (
          circularColumns.has(columnGroup as StoichiometryTableCircularColumnGroup) &&
          columnGroup !== filledColumnGroup
        ) {
          return '';
        }
        const columnIndex = columnGroupToIndexMap[columnGroup];
        return columnIndex != null ? {row: rowIndex, column: columnIndex} : '';
      });
      // This must match the size and expected values of StoichiometryCalculationCellDependencies
      return [
        '',
        toSpreadsheetBooleanValue(false),
        ...limitingReactantDependencies,
        ...componentDependencies,
      ];
    }

    // If not exactly 1 of the variables has been filled, give an error to the user.
    const columnsToFill = abbreviateList(
      [...circularColumns].map((columnGroup) => COLUMN_GROUP_TO_NAME_MAP[columnGroup] ?? ''),
      circularColumns.size,
      'or'
    );
    return [
      filledCircularColumns.length === 0
        ? `Must fill one of ${columnsToFill}`
        : `Must fill only one of ${columnsToFill}`,
    ];
  }

  override getTableSpecificCustomEvalArgs(
    tableView: CustomEvalTableView<
      StoichiometryStructuredTableMetadata,
      StoichiometryStructuredTableRowMetadata
    >,
    cellIndex: CellIndex,
    {toSpreadsheetIndex}: GetCustomEvalArgsOptions
  ): Array<string> {
    const customEvalDependencies = this.getCustomEvalDependencies(tableView, cellIndex);
    // If the dependency is a string, just return that and it will be converted to a TypedValue.
    // Otherwise, convert cell indexes to spreadsheet reference strings.
    const dependentSpreadsheetReferences = customEvalDependencies.map((dep) =>
      typeof dep === 'string' ? dep : `=${toSpreadsheetIndex(dep)}`
    );
    return dependentSpreadsheetReferences;
  }

  override getTableSpecificCustomEvalFn(
    tableView: CustomEvalTableView<
      StoichiometryStructuredTableMetadata,
      StoichiometryStructuredTableRowMetadata
    >,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: StoichiometryStructuredTableMetadata;
    rowMetadata: StoichiometryStructuredTableRowMetadata;
  }> | null {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    const {columnGroup} = column;
    if (!columnGroup) {
      return null;
    }
    switch (columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT:
        return {customEvalFn: limitingReactantCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA:
        return {customEvalFn: getMolecularFormulaColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.CHEMISTRY_IUPAC_NAME:
        return {customEvalFn: getIupacNameColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.CHEMISTRY_CAS_REGISTRY_NUMBER:
        return {customEvalFn: getCasRegistryNumberColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT:
        return {customEvalFn: getMolecularWeightColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY:
        return {customEvalFn: getMoleculePurityColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT:
        return {
          customEvalFn: getDefaultUnitCustomEval(DEFAULT_MOL_UNIT_SYMBOL),
          customEvalType: CustomEvalType.DEFAULT,
        };
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT:
        return {
          customEvalFn: getDefaultUnitCustomEval(DEFAULT_MASS_UNIT_SYMBOL),
          customEvalType: CustomEvalType.DEFAULT,
        };
      case StructuredTableColumnGroups.CHEMISTRY_MOLES_ACTUAL:
        return {customEvalFn: molesActualCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION:
      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT: {
        return {customEvalFn: stoichiometryConcentrationCustomEval, customEvalType: CustomEvalType.DEFAULT};
      }
      case StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE:
      case StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN:
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN: {
        return {
          customEvalFn: makeStoichiometryCalculationCustomEval(columnGroup),
          customEvalType: CustomEvalType.DEFAULT,
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_YIELD:
        return {customEvalFn: yieldCustomEval, customEvalType: CustomEvalType.DEFAULT};
      default:
        return null;
    }
  }

  override shouldSuppressTitleBar(_table: StoichiometryStructuredTable): boolean {
    return true;
  }

  override shouldShowFormulaBox(_table: StoichiometryStructuredTable): boolean {
    return true;
  }

  override enableCustomColumns(structuredTable: StoichiometryStructuredTable): boolean {
    return true;
  }
}
